// TODO fix typos issue with Node Tiptap
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { Node, mergeAttributes, nodeInputRule } from '@tiptap/core'
import { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { NodeViewProps } from '@tiptap/react'

export const CustomImageExtension = Node.create({
  name: 'image',
  group: 'inline',
  inline: true,
  atom: true,
  draggable: true,
  selectable: true,

  addOptions() {
    return {
      HTMLAttributes: {},
      minWidth: 50,
      maxWidth: null,
    }
  },

  addAttributes() {
    return {
      src: {
        default: null,
      },
      alt: {
        default: null,
      },
      title: {
        default: null,
      },
      width: {
        default: null,
      },
      height: {
        default: null,
      },
      align: {
        default: 'left',
      },
      style: {
        default: null,
      },
      emailTable: {
        default: false,
        parseHTML: () => false,
        renderHTML: () => null,
      },
    }
  },

  addCommands() {
    return {
      setImage:
        (attrs) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs,
          })
        },

      updateImage:
        (attrs) =>
        ({ tr, dispatch }) => {
          const { selection } = tr
          const node = tr.doc.nodeAt(selection.from)

          if (node && node.type.name === this.name) {
            if (dispatch) {
              tr.setNodeMarkup(selection.from, undefined, {
                ...node.attrs,
                ...attrs,
              })
            }

            return true
          }

          return false
        },
    }
  },

  renderHTML({ HTMLAttributes }) {
    const { align, ...imageAttrs } = HTMLAttributes

    const imgHtml = [
      'img',
      mergeAttributes(this.options.HTMLAttributes, imageAttrs),
    ]

    if (!align || align === 'left') {
      return imgHtml
    }

    return [
      'table',
      {
        border: '0',
        cellpadding: '0',
        cellspacing: '0',
        style: 'width: 100%; margin: 10px 0;',
        'data-align-table': 'true',
      },
      [
        'tr',
        {},
        [
          'td',
          {
            align: align,
            style: 'width: 100%;',
          },
          imgHtml,
        ],
      ],
    ]
  },

  parseHTML() {
    return [
      {
        tag: 'img[src]',
        getAttrs: (element) => {
          if (typeof element === 'string') return {}

          const el = element as HTMLElement

          const attrs: Record<string, string> = {}

          ;['src', 'alt', 'title', 'width', 'height'].forEach((attr) => {
            const value = el.getAttribute(attr)
            if (value) attrs[attr] = value
          })

          const style = el.getAttribute('style')
          if (style) attrs['style'] = style

          let align = 'left'
          const parentTable = el.closest('table[data-align-table="true"]')

          if (parentTable) {
            const td = el.closest('td')
            if (td) {
              const tdAlign = td.getAttribute('align')
              if (tdAlign) {
                align = tdAlign
              }
            }
          } else {
            align =
              el.getAttribute('align') ||
              (style &&
              style.includes('margin-left: auto') &&
              style.includes('margin-right: auto')
                ? 'center'
                : style && style.includes('float: right')
                ? 'right'
                : 'left')
          }

          if (align) attrs['align'] = align

          return attrs
        },
      },
      {
        tag: 'table[data-align-table="true"] img',
        getAttrs: (element) => {
          if (typeof element === 'string') return {}

          const el = element as HTMLElement
          const attrs: Record<string, string> = {}

          ;['src', 'alt', 'title', 'width', 'height', 'style'].forEach(
            (attr) => {
              const value = el.getAttribute(attr)
              if (value) attrs[attr] = value
            },
          )

          const td = el.closest('td')
          if (td) {
            const align = td.getAttribute('align')
            if (align) {
              attrs['align'] = align
            }
          }

          attrs['emailTable'] = true

          return attrs
        },
      },
    ]
  },

  addInputRules() {
    return [
      nodeInputRule({
        find: /!\[(.+|:?)]\((\S+)(?:(?:\s+)["'](\S+)["'])?\)/,
        type: this.type,
        getAttributes: (match) => {
          const [, alt, src, title] = match

          return {
            src,
            alt,
            title,
          }
        },
      }),
    ]
  },

  addNodeView() {
    return ({ node, editor, getPos }: NodeViewProps) => {
      const {
        view,
        options: { editable },
      } = editor

      const { src, alt, title, width, align, style } = node.attrs

      const dom = document.createElement('div')
      dom.classList.add('enhanced-image-wrapper')
      dom.style.position = 'relative'
      dom.style.margin = '10px 0'

      const img = document.createElement('img')
      img.src = src
      if (alt) img.alt = alt
      if (title) img.title = title
      if (width) img.style.width = `${width}px`
      if (style) img.setAttribute('style', style)

      img.style.maxWidth = '100%'

      dom.style.textAlign = align || 'left'

      dom.appendChild(img)

      if (!editable) {
        return { dom }
      }

      const controls = document.createElement('div')
      controls.classList.add('enhanced-image-controls')
      controls.style.display = 'none'
      controls.style.position = 'absolute'
      controls.style.top = '-54px'
      controls.style.left = '0'
      controls.style.right = '0'
      controls.style.background = '#f8f9fa'
      controls.style.border = '1px solid #ddd'
      controls.style.borderRadius = '4px'
      controls.style.padding = '4px 8px'
      controls.style.zIndex = '10'
      controls.style.textAlign = 'center'
      controls.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)'

      dom.appendChild(controls)

      let isResizing = false
      let startX = 0
      let startWidth = 0
      let currentAlignment = align || 'left'

      const cleanupResizeControls = () => {
        controls.style.display = 'none'

        const resizeDots = dom.querySelectorAll('.resize-dot')
        resizeDots.forEach((dot) => dot.remove())

        img.style.outline = ''
      }

      const createAlignmentControls = () => {
        controls.innerHTML = ''

        const alignBtns = document.createElement('div')
        alignBtns.style.display = 'flex'
        alignBtns.style.justifyContent = 'center'
        alignBtns.style.gap = '8px'

        const createAlignButton = (
          title: string,
          icon: string,
          alignment: string,
        ) => {
          const btn = document.createElement('button')
          const isActive = currentAlignment === alignment

          btn.style.background = isActive ? '#e9ecef' : 'none'
          btn.style.border = `1px solid ${isActive ? '#ced4da' : '#ddd'}`
          btn.style.borderRadius = '4px'
          btn.style.padding = '4px'
          btn.style.cursor = 'pointer'
          btn.innerHTML = icon
          btn.title = title

          btn.addEventListener('click', (e) => {
            e.preventDefault()
            e.stopPropagation()

            currentAlignment = alignment

            dom.style.textAlign = alignment

            if (typeof getPos === 'function') {
              const pos = getPos()
              if (pos !== undefined) {
                view.dispatch(
                  view.state.tr.setNodeAttribute(pos, 'align', alignment),
                )
              }
            }

            Array.from(alignBtns.children).forEach((child, index) => {
              const isNowActive =
                ['left', 'center', 'right'][index] === alignment
              ;(child as HTMLElement).style.background = isNowActive
                ? '#e9ecef'
                : 'none'
              ;(child as HTMLElement).style.borderColor = isNowActive
                ? '#ced4da'
                : '#ddd'
            })
          })

          return btn
        }

        const leftBtn = createAlignButton(
          'Align left',
          '<svg width="16" height="16" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><line x1="4" y1="6" x2="20" y2="6" /><line x1="4" y1="12" x2="14" y2="12" /><line x1="4" y1="18" x2="18" y2="18" /></svg>',
          'left',
        )

        const centerBtn = createAlignButton(
          'Align center',
          '<svg width="16" height="16" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><line x1="4" y1="6" x2="20" y2="6" /><line x1="8" y1="12" x2="16" y2="12" /><line x1="6" y1="18" x2="18" y2="18" /></svg>',
          'center',
        )

        const rightBtn = createAlignButton(
          'Align right',
          '<svg width="16" height="16" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><line x1="4" y1="6" x2="20" y2="6" /><line x1="10" y1="12" x2="20" y2="12" /><line x1="6" y1="18" x2="20" y2="18" /></svg>',
          'right',
        )

        alignBtns.appendChild(leftBtn)
        alignBtns.appendChild(centerBtn)
        alignBtns.appendChild(rightBtn)
        controls.appendChild(alignBtns)

        controls.style.display = 'block'
      }

      const createResizeDots = () => {
        const dotSize = '9px'
        const dotPositions = [
          {
            name: 'top-left',
            top: '-4px',
            left: '-4px',
            cursor: 'nwse-resize',
          },
          {
            name: 'top-right',
            top: '-4px',
            right: '-4px',
            cursor: 'nesw-resize',
          },
          {
            name: 'bottom-left',
            bottom: '-4px',
            left: '-4px',
            cursor: 'nesw-resize',
          },
          {
            name: 'bottom-right',
            bottom: '-4px',
            right: '-4px',
            cursor: 'nwse-resize',
          },
        ]

        dotPositions.forEach((pos) => {
          const dot = document.createElement('div')
          dot.classList.add('resize-dot')
          dot.style.position = 'absolute'
          dot.style.width = dotSize
          dot.style.height = dotSize
          dot.style.border = '1.5px solid #6C6C6C'
          dot.style.borderRadius = '50%'
          dot.style.background = '#fff'

          if (pos.top) dot.style.top = pos.top
          if (pos.left) dot.style.left = pos.left
          if (pos.right) dot.style.right = pos.right
          if (pos.bottom) dot.style.bottom = pos.bottom

          dot.style.cursor = pos.cursor

          dot.addEventListener('mousedown', (e) => {
            e.preventDefault()
            e.stopPropagation()

            isResizing = true
            startX = e.clientX
            startWidth = img.width || parseInt(img.style.width || '0', 10)

            const handleResize = (clientX: number) => {
              if (!isResizing) return

              const isLeftSide = pos.name.includes('left')
              const deltaX = isLeftSide ? -(clientX - startX) : clientX - startX

              const minWidth = 50
              const maxWidth = null

              let newWidth = Math.max(minWidth, startWidth + deltaX)
              if (maxWidth !== null) {
                newWidth = Math.min(newWidth, maxWidth)
              }

              img.style.width = `${newWidth}px`
            }

            const handleMouseUp = () => {
              if (isResizing) {
                isResizing = false

                if (typeof getPos === 'function') {
                  const pos = getPos()
                  if (pos !== undefined) {
                    const width = img.width

                    view.dispatch(
                      view.state.tr.setNodeAttribute(pos, 'width', width),
                    )
                  }
                }
              }

              document.removeEventListener('mousemove', handleMouseMove)
              document.removeEventListener('mouseup', handleMouseUp)
            }

            const handleMouseMove = (e: MouseEvent) => handleResize(e.clientX)

            document.addEventListener('mousemove', handleMouseMove)
            document.addEventListener('mouseup', handleMouseUp)
          })

          img.parentElement?.appendChild(dot)
        })
      }

      img.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()

        cleanupResizeControls()

        img.style.outline = '1px dashed #6C6C6C'

        createResizeDots()
        createAlignmentControls()

        const handleDocumentClick = (e: MouseEvent) => {
          const target = e.target as HTMLElement

          if (
            target !== img &&
            !controls.contains(target) &&
            !dom.querySelector('.resize-dot')?.contains(target)
          ) {
            cleanupResizeControls()
            document.removeEventListener('click', handleDocumentClick)
          }
        }

        document.addEventListener('click', handleDocumentClick)
      })

      return {
        dom,
        update: (updatedNode: ProseMirrorNode) => {
          if (updatedNode.attrs.src !== src) {
            img.src = updatedNode.attrs.src
          }

          if (updatedNode.attrs.alt !== alt) {
            img.alt = updatedNode.attrs.alt || ''
          }

          if (updatedNode.attrs.title !== title) {
            img.title = updatedNode.attrs.title || ''
          }

          if (updatedNode.attrs.width !== width) {
            img.style.width = updatedNode.attrs.width
              ? `${updatedNode.attrs.width}px`
              : ''
          }

          if (updatedNode.attrs.style !== style) {
            if (updatedNode.attrs.style) {
              img.setAttribute('style', updatedNode.attrs.style)
              img.style.maxWidth = '100%'
            }
          }

          if (updatedNode.attrs.align !== align) {
            currentAlignment = updatedNode.attrs.align || 'left'
            dom.style.textAlign = currentAlignment
          }

          return true
        },
        destroy: () => {
          document.removeEventListener('click', () => {})
          img.removeEventListener('click', () => {})
        },
      }
    }
  },
})
